// GoogleView.jsx — the Google reviews page.
//
// REBUILT to Braeden's 8/12 spec, which stripped the earlier three-tab version
// down to three things:
//
//   "on the top needs to be a picture of the profile, business name, and just a
//    what the stars are, how many reviews in total, and how many collected this
//    week in green like a plus 7 or something, below get rid of everything and
//    just put a link to edit profile so it opens up googles platforms, below
//    that needs to be Request text, so if they click on it, the whole text pops
//    up that gets sent, so if they want to edit it they can"
//
// So: header card → one Edit-profile link → Request text that opens the real
// message in a dialog you can edit. Everything else (Photos, Services, All
// reviews, Reply-to-reviews, the tab bar, the ask-Aria box) is GONE. Those rows
// were links to the same Google page wearing different hats, and a review page
// that needs a tab bar is a review page nobody reads.
//
// The +N this week is the number he actually wants to see — reviews are a
// weekly grind, and a running total never tells you whether this week worked.
//
// Google Business Profile is not connected yet (owner: "add the api later"), so
// the header reads as unconnected rather than inventing stars. Wire
// /api/google/profile and it fills in with no layout change; supply
// `reviewsThisWeek` and the green pill appears on its own.

import React from 'react';

// THE SEQUENCE, not a single text (Braeden 8/12).
//
// The order is the whole point and it is easy to wreck. Message 1 asks how the
// work is HOLDING UP and asks for nothing — so the customer answers a human
// being instead of ignoring a marketing blast. Only once they have replied
// does message 2 ask for the review, and by then they have already told you it
// went well. Message 3 is the single nudge if no review ever lands.
//
// Never collapse these into one text. "Thanks, leave us a review" cold is the
// version everybody ignores.
// WRITTEN THE WAY A PERSON TEXTS (Braeden 8/12): "nobody puts - they use , if
// anything". No em-dashes anywhere in a message body. Commas and full stops
// only. An em-dash in a text is a tell that software wrote it.
//
// The review LINK is its own separate message, never tacked onto the ask. That
// is how people actually send a link, and a bare link on its own line is far
// less likely to get a text filtered by the carrier than one buried mid
// sentence.
const STEPS = [
  {
    key: 'checkin',
    title: 'First text',
    when: 'Sent 2 hours after the job is marked done',
    branch: null,
    body: `Hey {{first_name}}, {{owner_name}} with {{business}}, how's the new {{job}} holding up?`,
  },
  {
    key: 'ask',
    title: 'After they reply',
    when: 'Sent as soon as they answer',
    branch: 'If they reply',
    // "helps other people trust our business, not helps a small shop like ours"
    // (Braeden 8/12). Asking on behalf of the next customer beats asking for a
    // favour: it gives the reviewer a reason that is not pity, and it is true.
    body: `Thanks for having us out. If you've got a quick minute, would you mind leaving us a review? It helps other people trust our business.`,
    linkKey: 'askLink',
    linkBody: `{{review_link}}`,
  },
  {
    key: 'nudge',
    title: 'If no review',
    when: 'Sent once, 3 days later, then she stops',
    branch: 'If the checker still sees no review',
    body: `Hey {{first_name}}, no worries if you're busy. If you get a second, a quick review would mean a lot.`,
    linkKey: 'nudgeLink',
    linkBody: `{{review_link}}`,
  },
];

const STORE_KEY = 'howto_review_sequence';

export default function GoogleView({ businessName }) {
  const [profile, setProfile] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [open, setOpen] = React.useState(false);
  const defaults = React.useMemo(
    () => STEPS.reduce((a, s) => {
      a[s.key] = s.body;
      if (s.linkKey) a[s.linkKey] = s.linkBody;
      return a;
    }, {}), []);
  const [seq, setSeq] = React.useState(() => {
    try {
      const raw = JSON.parse(localStorage.getItem(STORE_KEY) || 'null');
      // Merge over defaults so a stored copy from an older version (or a
      // half-written one) can never leave a step blank.
      return raw && typeof raw === 'object' ? { ...defaults, ...raw } : { ...defaults };
    } catch (_) { return { ...defaults }; }
  });
  const [draft, setDraft] = React.useState(seq);
  const [savedTick, setSavedTick] = React.useState(false);

  React.useEffect(() => {
    let dead = false;
    (async () => {
      try {
        const ctl = new AbortController();
        const t = setTimeout(() => ctl.abort(), 8000);
        const r = await fetch('/api/google/profile', { signal: ctl.signal });
        clearTimeout(t);
        if (!r.ok) throw new Error(String(r.status));
        const j = await r.json();
        if (!dead) setProfile(j && j.ok !== false ? j : null);
      } catch (_) {
        if (!dead) setProfile(null); // not connected — expected today
      } finally {
        if (!dead) setLoading(false);
      }
    })();
    return () => { dead = true; };
  }, []);

  // Esc closes the dialog — a popup you can only leave by hunting for the X is
  // the kind of thing that makes an owner stop opening it.
  React.useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  const connected = !!(profile && (profile.rating || profile.name));
  const name = (profile && profile.name) || businessName || 'Your business';
  const total = Number((profile && profile.reviewCount) || 0);
  const week = Number((profile && profile.reviewsThisWeek) || 0);
  const editUrl = (profile && profile.editUrl) || 'https://business.google.com/';

  const fill = (s) => String(s || '')
    .replace(/\{\{first_name\}\}/g, 'Mike')
    .replace(/\{\{owner_name\}\}/g, (profile && profile.ownerName) || 'Braeden')
    .replace(/\{\{business\}\}/g, name)
    .replace(/\{\{job\}\}/g, (profile && profile.lastJob) || 'roof')
    .replace(/\{\{review_link\}\}/g, 'g.page/r/review');

  function openDialog() { setDraft({ ...seq }); setSavedTick(false); setOpen(true); }
  function save() {
    setSeq(draft);
    try { localStorage.setItem(STORE_KEY, JSON.stringify(draft)); } catch (_) {}
    setSavedTick(true);
    setTimeout(() => setOpen(false), 650);
  }

  return (
    <div className="gv">
      <style>{CSS}</style>

      {/* ── 1. PROFILE: photo · name · stars · total · +this week ───── */}
      <div className="gv-profile">
        <div className="gv-photo">
          {connected && profile.photo
            ? <img src={profile.photo} alt={`${name} on Google`} />
            : <span className="gv-photo__ph">{String(name).slice(0, 1).toUpperCase()}</span>}
        </div>

        <div className="gv-pmeta">
          <div className="gv-pname">{name}</div>
          {loading ? (
            <div className="gv-prate gv-prate--muted">Checking Google…</div>
          ) : connected ? (
            <div className="gv-prate">
              <b>{Number(profile.rating).toFixed(1)}</b>
              <Stars value={Number(profile.rating)} />
              <span className="gv-pcount">{total.toLocaleString()} reviews</span>
              {week > 0 && <span className="gv-week">+{week} this week</span>}
            </div>
          ) : (
            <div className="gv-prate gv-prate--muted">Google Business Profile not connected</div>
          )}
        </div>
      </div>

      {/* ── 2. ONE LINK OUT ─────────────────────────────────────────── */}
      <a className="gv-link" href={editUrl} target="_blank" rel="noopener noreferrer">
        <span className="gv-link__label">Edit profile</span>
        <span className="gv-link__go">Open on Google &rarr;</span>
      </a>

      {/* ── 3. REQUEST TEXT — opens the real message, editable ──────── */}
      <button type="button" className="gv-link gv-link--btn" onClick={openDialog}>
        <span className="gv-link__labelwrap">
          <span className="gv-link__label">Request text</span>
          {/* say up front that this is a sequence, not one text */}
          <span className="gv-link__sub">3 messages — check in, then ask, then one nudge</span>
        </span>
        <span className="gv-link__go">View &amp; edit &rarr;</span>
      </button>

      {open && (
        <div className="gv-modal" onMouseDown={(e) => { if (e.target === e.currentTarget) setOpen(false); }}>
          <div className="gv-sheet" role="dialog" aria-modal="true" aria-label="Review request text">
            <div className="gv-sheet__head">
              <span>Review request text</span>
              <button type="button" className="gv-x" onClick={() => setOpen(false)} aria-label="Close">✕</button>
            </div>

            {/* THE WHOLE PROCESS, top to bottom: what goes first, what happens
                if they reply, and what happens if no review ever lands. Each
                message is shown as the customer receives it, then editable
                directly underneath it. */}
            {STEPS.map((s, i) => (
              <div className="gv-step" key={s.key}>
                {s.branch && (
                  <div className="gv-branch"><i /><span>{s.branch}</span><i /></div>
                )}
                <div className="gv-step__head">
                  <span className="gv-step__n">{i + 1}</span>
                  <span className="gv-step__t">{s.title}</span>
                </div>

                <div className="gv-phone">
                  <div className="gv-phone__bar"><span className="gv-phone__from">{name}</span></div>
                  <div className="gv-phone__body">
                    <div className="gv-bubble">{fill(draft[s.key])}</div>
                    {/* the link lands as its OWN text, a beat later, the way a
                        person sends one */}
                    {s.linkKey && <div className="gv-bubble gv-bubble--link">{fill(draft[s.linkKey])}</div>}
                    <div className="gv-stamp">{s.when}</div>
                  </div>
                </div>

                <textarea
                  className="gv-edit"
                  rows={3}
                  value={draft[s.key]}
                  onChange={(e) => { setDraft({ ...draft, [s.key]: e.target.value }); setSavedTick(false); }}
                  spellCheck
                />
                {s.linkKey && (
                  <>
                    <div className="gv-edit__cap">Second text, just the link</div>
                    <textarea
                      className="gv-edit gv-edit--link"
                      rows={1}
                      value={draft[s.linkKey]}
                      onChange={(e) => { setDraft({ ...draft, [s.linkKey]: e.target.value }); setSavedTick(false); }}
                      spellCheck={false}
                    />
                  </>
                )}
              </div>
            ))}

            <div className="gv-hint">
              {'{{first_name}}'} · {'{{owner_name}}'} · {'{{business}}'} · {'{{job}}'} · {'{{review_link}}'} fill in per customer.
            </div>

            <div className="gv-actions">
              <button type="button" className="gv-btn gv-btn--ghost"
                onClick={() => setDraft({ ...defaults })}>Reset to default</button>
              <button type="button" className="gv-btn"
                disabled={STEPS.some((s) => !String(draft[s.key] || '').trim())}
                onClick={save}>
                {savedTick ? 'Saved ✓' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Stars({ value }) {
  const v = Math.max(0, Math.min(5, Number(value) || 0));
  return (
    <span className="gv-stars" aria-label={`${v} out of 5`}>
      {[0, 1, 2, 3, 4].map((i) => (
        <i key={i} className={v >= i + 0.75 ? 'on' : v >= i + 0.25 ? 'half' : ''}>★</i>
      ))}
    </span>
  );
}

const CSS = `
.gv { padding:26px clamp(14px,3vw,34px) 60px; max-width:820px; margin:0 auto; }

.gv-profile { background:#fff; border-radius:20px; padding:22px clamp(18px,2.6vw,30px);
  box-shadow:0 1px 2px rgba(16,32,64,.06), 0 6px 18px rgba(16,32,64,.05);
  display:flex; gap:20px; align-items:center; }
.gv-photo { width:96px; height:96px; border-radius:16px; flex:none; overflow:hidden;
  background:#eef1f6; display:grid; place-items:center; }
.gv-photo img { width:100%; height:100%; object-fit:cover; display:block; }
.gv-photo__ph { font-size:38px; font-weight:800; color:#94a3b8; }
.gv-pmeta { min-width:0; }
.gv-pname { font-size:23px; font-weight:800; letter-spacing:-.4px; color:#0e1420; }
.gv-prate { display:flex; align-items:center; gap:9px; margin-top:7px; flex-wrap:wrap;
  font-size:15px; color:#0e1420; }
.gv-prate b { font-size:20px; font-weight:800; }
.gv-prate--muted { color:#6b7280; font-size:14.5px; }
.gv-pcount { color:#6b7280; }
/* the number he asked to see: what THIS WEEK produced, in green */
.gv-week { background:#dcfce7; color:#15803d; font-weight:800; font-size:13px;
  padding:4px 11px; border-radius:999px; letter-spacing:-.1px; }
.gv-stars { display:inline-flex; gap:1px; font-size:16px; line-height:1; }
.gv-stars i { color:#d5dae2; font-style:normal; }
.gv-stars i.on { color:#fbbf24; }
.gv-stars i.half { background:linear-gradient(90deg,#fbbf24 50%,#d5dae2 50%);
  -webkit-background-clip:text; background-clip:text; color:transparent; }

/* the two rows below — same shape so they read as one pair */
.gv-link { display:flex; align-items:center; justify-content:space-between; gap:14px;
  width:100%; margin-top:14px; background:#fff; border:1px solid #e7eaef; border-radius:16px;
  padding:18px 20px; text-decoration:none; cursor:pointer; text-align:left;
  font-family:inherit; transition:border-color .18s ease, box-shadow .18s ease; }
.gv-link:hover { border-color:#cfd6e0; box-shadow:0 3px 14px rgba(16,32,64,.06); }
.gv-link__labelwrap { display:flex; flex-direction:column; gap:3px; min-width:0; }
.gv-link__label { font-size:16.5px; font-weight:700; color:#0e1420; letter-spacing:-.2px; }
.gv-link__sub { font-size:13px; color:#6b7280; letter-spacing:-.1px; }
.gv-link__go { font-size:14.5px; font-weight:600; color:#2563eb; white-space:nowrap; }
.gv-link--btn { appearance:none; }

/* dialog */
.gv-modal { position:fixed; inset:0; background:rgba(10,16,28,.42); z-index:60;
  display:flex; align-items:center; justify-content:center; padding:18px;
  animation:gvfade .18s ease-out; }
@keyframes gvfade { from{opacity:0} to{opacity:1} }
.gv-sheet { background:#fff; border-radius:20px; width:100%; max-width:520px;
  padding:18px 20px 20px; box-shadow:0 20px 60px rgba(10,16,28,.28);
  max-height:88vh; overflow-y:auto; animation:gvup .26s cubic-bezier(.32,.72,0,1); }
@keyframes gvup { from{transform:translateY(14px);opacity:0} to{transform:none;opacity:1} }
.gv-sheet__head { display:flex; align-items:center; justify-content:space-between;
  font-size:16px; font-weight:800; color:#0e1420; margin-bottom:14px; }
.gv-x { border:0; background:#f1f4f9; color:#4b5563; width:30px; height:30px;
  border-radius:50%; cursor:pointer; font-size:14px; line-height:1; }
.gv-x:hover { background:#e5e9f0; }

.gv-phone { background:#f6f7fa; border:1px solid #e7eaef; border-radius:14px; overflow:hidden; }
.gv-phone__bar { padding:9px 14px; border-bottom:1px solid #e7eaef; background:#fff; }
.gv-phone__from { font-size:12.5px; font-weight:700; color:#6b7280; }
.gv-phone__body { padding:14px; }
.gv-bubble { background:#34c759; color:#fff; padding:10px 14px; border-radius:18px;
  font-size:15px; line-height:1.35; max-width:92%; letter-spacing:-.2px; width:fit-content; }
/* the link arrives as a second, separate message — sits tight under the first
   the way a follow-up bubble does on a phone */
.gv-bubble--link { margin-top:3px; }
.gv-edit__cap { font-size:12px; color:#8e8e93; margin:8px 0 -6px; font-weight:600; }
.gv-edit--link { font-family:ui-monospace,SFMono-Regular,Menlo,monospace; font-size:13.5px; }
.gv-stamp { font-size:12px; color:#8e8e93; margin-top:9px; }

/* background MUST be explicit. Without it the textarea inherited the app's dark
   theme (#3b3b3b) while this CSS set near-black text — dark on dark, about a
   1.2:1 ratio, the text was there and unreadable. Same class of bug as the
   invisible lead rows: a colour set on one side of a pair and not the other. */
.gv-edit { width:100%; margin-top:14px; border:1px solid #d9dde4; border-radius:12px;
  padding:12px 13px; font-size:14.5px; font-family:inherit;
  background:#fff; color:#0e1420; resize:vertical;
  line-height:1.45; outline:none; }
.gv-edit::placeholder { color:#9aa1ab; }

/* the sequence — numbered steps with the BRANCH CONDITION shown between them,
   so the owner can see what triggers each text without reading a manual */
.gv-step + .gv-step { margin-top:6px; }
.gv-step__head { display:flex; align-items:center; gap:9px; margin:14px 0 8px; }
.gv-step__n { width:21px; height:21px; border-radius:50%; background:#0e1420; color:#fff;
  font-size:12px; font-weight:800; display:grid; place-items:center; flex:none; }
.gv-step__t { font-size:14.5px; font-weight:800; color:#0e1420; letter-spacing:-.2px; }
.gv-branch { display:flex; align-items:center; gap:10px; margin:16px 0 2px; }
.gv-branch i { flex:1; height:1px; background:#e7eaef; }
.gv-branch span { font-size:12px; font-weight:700; color:#8e8e93; letter-spacing:.02em;
  white-space:nowrap; }
.gv-edit:focus { border-color:#2563eb; box-shadow:0 0 0 3px rgba(37,99,235,.12); }
.gv-hint { font-size:12.5px; color:#8e8e93; margin-top:7px; }
.gv-actions { display:flex; gap:10px; justify-content:flex-end; margin-top:16px; }
.gv-btn { border:0; border-radius:11px; padding:11px 20px; font-size:14.5px; font-weight:700;
  background:#2563eb; color:#fff; cursor:pointer; font-family:inherit; }
.gv-btn:disabled { opacity:.5; cursor:default; }
.gv-btn--ghost { background:#f1f4f9; color:#374151; }

@media (max-width:560px) {
  .gv-profile { gap:15px; padding:18px; }
  .gv-photo { width:72px; height:72px; border-radius:14px; }
  .gv-pname { font-size:20px; }
}
`;
